import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  engagementProfileQuery,
  meQuery,
  serviceNowConnectionsQuery,
  setEngagementPlatform,
  setEngagementTimeZone,
  updateProfile,
} from '../api/jedi'
import { useStoredEngagement } from '../lib/engagement'
import { DEFAULT_ZONE } from '../lib/personTime'
import { canRecordRelease, refusalOf } from '../shell/EngagementAccess'
import { IntegrationsSection, productionRelease } from '../shell/ServiceNowConnections'
import { CategoriesSection } from '../shell/EngagementCategories'
import { PhasesSection } from '../shell/EngagementPhases'
import {
  platformBody,
  platformOf,
  productName,
  releaseName,
  usePlatformChoices,
  type PlatformChoice,
} from '../shell/PlatformFields'
import { zoneGroups, zoneLabel } from '../shell/timeZones'

type Section = 'you' | 'defaults' | 'phases' | 'categories' | 'release' | 'integrations'
const SECTIONS: readonly Section[] = ['you', 'defaults', 'phases', 'categories', 'release', 'integrations']

export const Route = createFileRoute('/_shell/settings')({
  component: Settings,
  // A section can be linked to: cold start sends a person to check the phases.
  validateSearch: (search: Record<string, unknown>): { section?: Section } => {
    const section = typeof search.section === 'string' ? search.section : undefined
    return SECTIONS.includes(section as Section) ? { section: section as Section } : {}
  },
})

/*
  SETTINGS (wave 23; ruled by Erik 2026-09-22). The design's Settings screen,
  built to exactly what has been asked for, and nothing unbuilt is drawn as
  though it were. Two of its sections exist:

  - YOU, the Profile card's two rows, Name and Time zone. Kept on the server,
    per person, so they follow the person to every device and engagement.
  - THIS ENGAGEMENT › DEFAULTS, the design's first section of the group,
    holding the one default built: the customer's time zone (2026-10-09).
  - THIS ENGAGEMENT › PHASES (ADR-0061, 2026-10-20), the engagement's own
    phases in their two kinds; see its own file.
  - THIS ENGAGEMENT › CATEGORIES (ADR-0030's 2026-10-21 amendment), the
    engagement's own task categories, set like its phases; see its own file.
  - THIS ENGAGEMENT › RELEASE (RFC-0013's 2026-10-07 amendment), which the
    design's This engagement group does not draw. Built from its own card and
    row patterns, and logged for design in the pull request that added it.
  - THIS ENGAGEMENT › INTEGRATIONS (plan step 3.5), the design's Connected
    systems, holding the customer's ServiceNow instances; see its own file.

  The rail splits them as the design does: what follows you, and what belongs
  to the engagement that is open.
*/
function Settings() {
  const engagement = useStoredEngagement()
  const linked = Route.useSearch().section
  const [section, setSection] = useState<Section>(linked ?? 'you')
  useEffect(() => { if (linked) setSection(linked) }, [linked])
  const shown = engagement ? section : 'you'

  return (
    <section className="surface settings">
      <nav className="settings-nav" aria-label="Settings sections">
        <p className="settings-nav-group">You</p>
        <p className="settings-nav-scope">follows you everywhere</p>
        <NavItem active={shown === 'you'} onPick={() => setSection('you')}>
          You
        </NavItem>
        {engagement && (
          <>
            <p className="settings-nav-group">This engagement</p>
            <p className="settings-nav-scope">{engagement.name}</p>
            <NavItem active={shown === 'defaults'} onPick={() => setSection('defaults')}>
              Defaults
            </NavItem>
            <NavItem active={shown === 'phases'} onPick={() => setSection('phases')}>
              Phases
            </NavItem>
            <NavItem active={shown === 'categories'} onPick={() => setSection('categories')}>
              Categories
            </NavItem>
            <NavItem active={shown === 'release'} onPick={() => setSection('release')}>
              Release
            </NavItem>
            <NavItem active={shown === 'integrations'} onPick={() => setSection('integrations')}>
              Integrations
            </NavItem>
          </>
        )}
      </nav>

      <div className="settings-main">
        {shown === 'defaults' && engagement ? (
          <DefaultsSection engagementId={engagement.id} engagementName={engagement.name} />
        ) : shown === 'phases' && engagement ? (
          <PhasesSection engagementId={engagement.id} engagementName={engagement.name} />
        ) : shown === 'categories' && engagement ? (
          <CategoriesSection engagementId={engagement.id} engagementName={engagement.name} />
        ) : shown === 'release' && engagement ? (
          <ReleaseSection engagementId={engagement.id} engagementName={engagement.name} />
        ) : shown === 'integrations' && engagement ? (
          <IntegrationsSection engagementId={engagement.id} engagementName={engagement.name} />
        ) : (
          <YouSection />
        )}
      </div>
    </section>
  )
}

function NavItem({ active, onPick, children }: { active: boolean; onPick: () => void; children: string }) {
  return (
    <button
      type="button"
      className={`settings-nav-item${active ? ' settings-nav-item-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onPick}
    >
      {children}
    </button>
  )
}

/*
  SETTINGS › YOU. The time zone is the day every date in the app counts in
  (RAID #482): Today, due today, overdue, and what the chat reads as "today".
  Eastern until a person picks one, because most of Intact works there.
*/
function YouSection() {
  const queryClient = useQueryClient()
  const me = useQuery(meQuery)
  const groups = useMemo(() => zoneGroups(), [])
  const [name, setName] = useState('')
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    if (me.data) setName(me.data.name)
  }, [me.data])

  const save = useMutation({
    mutationFn: updateProfile,
    onSuccess: (next, asked) => {
      queryClient.setQueryData(meQuery.queryKey, next)
      // A new zone moves "today" for every surface, so everything reads again.
      if (asked.timeZone) void queryClient.invalidateQueries()
      setSaid(asked.timeZone ? `Saved. Your day now counts in ${zoneLabel(next.timeZone)}.` : 'Saved.')
    },
    onError: (error) => setSaid(error instanceof Error ? error.message : 'That did not save.'),
  })

  const zone = me.data?.timeZone ?? DEFAULT_ZONE
  const known = groups.some((g) => g.zones.some((z) => z.id === zone))
  const saveName = () => {
    if (!me.data || name.trim() === me.data.name) return
    save.mutate({ name: name.trim() })
  }

  return (
    <>
      <header className="settings-head">
        <h1>You</h1>
        <p className="surface-sub">How you appear, and the day your dates count in. Personal: nobody else sees these settings.</p>
      </header>

      <div className="settings-card">
        <div className="settings-card-head">
          <span className="settings-card-title">Profile</span>
          <span className="settings-card-scope">Applies to you</span>
        </div>

        <div className="settings-row">
          <label className="settings-row-text" htmlFor="settings-name">
            <span className="settings-row-title">Name</span>
            <span className="settings-row-detail">
              How you appear across J.E.D.I., and the name written on a task you take.
              {me.data?.signInName && me.data.signInName !== me.data.name
                ? ` Clear it to go back to ${me.data.signInName}.`
                : ''}
            </span>
          </label>
          <input
            id="settings-name"
            className="settings-input"
            value={name}
            maxLength={80}
            disabled={!me.data || save.isPending}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </div>

        <div className="settings-row">
          <label className="settings-row-text" htmlFor="settings-zone">
            <span className="settings-row-title">Time zone</span>
            <span className="settings-row-detail">
              Today, due dates and every time J.E.D.I. shows you are counted in this zone.
            </span>
          </label>
          <select
            id="settings-zone"
            className="settings-select"
            value={zone}
            disabled={!me.data || save.isPending}
            onChange={(e) => save.mutate({ timeZone: e.target.value })}
          >
            {!known && <option value={zone}>{zoneLabel(zone)}</option>}
            {groups.map((group) => (
              <optgroup key={group.region} label={group.region}>
                {group.zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {said && <p className="settings-said" role="status">{said}</p>}
    </>
  )
}

/*
  SETTINGS › THIS ENGAGEMENT › DEFAULTS (Erik, 2026-10-09: "An engagement time
  zone"). The design's Defaults section, holding the one default built: the
  time zone the customer works in, which is what off-hours means for work
  J.E.D.I. schedules against the customer's systems. Set by the same people as
  the release; everybody else sees the same picker, read-only, and who sets it.
  Eastern until somebody does, as a person's day is.
*/
function DefaultsSection({ engagementId, engagementName }: { engagementId: string; engagementName: string }) {
  const queryClient = useQueryClient()
  const me = useQuery(meQuery)
  const profile = useQuery(engagementProfileQuery(engagementId))
  const groups = useMemo(() => zoneGroups(), [])
  const [said, setSaid] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (zone: string) => setEngagementTimeZone(engagementId, zone),
    onSuccess: (updated) => {
      queryClient.setQueryData(engagementProfileQuery(engagementId).queryKey, updated)
      // Each connection's schedules moved with it, so their next runs read again.
      void queryClient.invalidateQueries({ queryKey: serviceNowConnectionsQuery(engagementId).queryKey })
      setSaid(`Saved. Scheduled work runs off-hours in ${zoneLabel(updated.timeZone ?? DEFAULT_ZONE)}.`)
    },
    onError: (error) => setSaid(refusalOf(error)),
  })

  const canSet = canRecordRelease(me.data, engagementId)
  const zone = profile.data?.timeZone ?? DEFAULT_ZONE
  const known = groups.some((g) => g.zones.some((z) => z.id === zone))

  return (
    <>
      <header className="settings-head">
        <h1>Defaults</h1>
        <p className="surface-sub">How {engagementName} keeps time.</p>
      </header>

      <div className="settings-card">
        <div className="settings-card-head">
          <span className="settings-card-title">Time</span>
          <span className="settings-card-scope">This engagement</span>
        </div>

        <div className="settings-row">
          <label className="settings-row-text" htmlFor="settings-engagement-zone">
            <span className="settings-row-title">Time zone</span>
            <span className="settings-row-detail">Where the customer works. Scheduled work runs off-hours here.</span>
          </label>
          <select
            id="settings-engagement-zone"
            className="settings-select"
            value={zone}
            disabled={!canSet || !profile.data || save.isPending}
            onChange={(e) => save.mutate(e.target.value)}
          >
            {!known && <option value={zone}>{zoneLabel(zone)}</option>}
            {groups.map((group) => (
              <optgroup key={group.region} label={group.region}>
                {group.zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {!canSet && me.data && <p className="settings-note">Set by this engagement&rsquo;s approvers.</p>}
      {said && <p className="settings-said" role="status">{said}</p>}
    </>
  )
}

/*
  SETTINGS › THIS ENGAGEMENT › RELEASE (RFC-0013's 2026-10-07 amendment). The
  release this engagement's customer runs, and the one it is upgrading to:
  J.E.D.I. answers from the first and compares the two for upgrade questions.

  Set by the people who decide on the engagement, and by the people who run the
  environment (Erik, 2026-10-07). Everybody else on it sees the same pickers,
  read-only, and why. What it is delivered on is the administrator's, so it is
  named here rather than offered. Each choice saves as it is made, as the time
  zone does; the pickers share their one rule with the creation dialog and
  Administration, so "Upgrading to" only ever offers later releases.

  What the production instance reports is said under the pickers, never put in
  them: with nothing recorded, J.E.D.I. answers from it (Erik, 2026-10-09), and
  where it differs from what is recorded, the recorded release still governs.
*/
function ReleaseSection({ engagementId, engagementName }: { engagementId: string; engagementName: string }) {
  const queryClient = useQueryClient()
  const me = useQuery(meQuery)
  const profile = useQuery(engagementProfileQuery(engagementId))
  const connections = useQuery(serviceNowConnectionsQuery(engagementId))
  const [said, setSaid] = useState<string | null>(null)
  // What was just chosen, shown while it saves, so a picker does not spring back to the old
  // release for the moment the request takes; dropped once the server answers either way.
  const [choosing, setChoosing] = useState<PlatformChoice | null>(null)
  const recorded = choosing ?? platformOf(profile.data ?? undefined)

  const save = useMutation({
    mutationFn: (next: PlatformChoice) => setEngagementPlatform(engagementId, platformBody(next)),
    onSuccess: (updated) => {
      queryClient.setQueryData(engagementProfileQuery(engagementId).queryKey, updated)
      setChoosing(null)
      setSaid('Saved.')
    },
    onError: (error) => {
      setChoosing(null)
      setSaid(refusalOf(error))
    },
  })
  const { products, product, releases, later, note, choose } = usePlatformChoices(recorded, (next) => {
    setChoosing(next)
    save.mutate(next)
  })

  const canSet = canRecordRelease(me.data, engagementId)
  const deliveredOn = productName(products, profile.data?.product)
  const locked = !canSet || !profile.data || save.isPending
  const reported = productionRelease(connections.data)
  const reportedName = reported ? releaseName(products, recorded.product, reported) : null
  const instanceNote = !reportedName || reported === recorded.release
    ? null
    : recorded.release
      ? `The production instance runs ${reportedName}.`
      : `Nothing is recorded, so J.E.D.I. answers from ${reportedName}, which the production instance runs.`

  return (
    <>
      <header className="settings-head">
        <h1>Release</h1>
        <p className="surface-sub">
          The release {engagementName}&rsquo;s customer runs. J.E.D.I. answers from it.
        </p>
      </header>

      {profile.data && !profile.data.product ? (
        <p className="settings-note">
          What this engagement is delivered on is not recorded yet. The people who run this
          environment set it, in Administration.
        </p>
      ) : (
        <div className="settings-card">
          <div className="settings-card-head">
            <span className="settings-card-title">{deliveredOn ?? 'Delivered on'}</span>
            <span className="settings-card-scope">This engagement</span>
          </div>

          <div className="settings-row">
            <label className="settings-row-text" htmlFor="settings-release">
              <span className="settings-row-title">{product?.display ?? 'Product'} release</span>
              <span className="settings-row-detail">The release the customer runs today.</span>
            </label>
            <select
              id="settings-release"
              className="settings-select"
              value={recorded.release}
              disabled={locked}
              onChange={(e) => choose({ release: e.target.value })}
            >
              <option value="">Not recorded</option>
              {releases.map((r) => (
                <option key={r.id} value={r.id}>{r.display}</option>
              ))}
            </select>
          </div>

          <div className="settings-row">
            <label className="settings-row-text" htmlFor="settings-upgrade">
              <span className="settings-row-title">Upgrading to</span>
              <span className="settings-row-detail">When this engagement takes them to a later release.</span>
            </label>
            <select
              id="settings-upgrade"
              className="settings-select"
              value={recorded.upgradingTo}
              disabled={locked}
              onChange={(e) => choose({ upgradingTo: e.target.value })}
            >
              <option value="">No upgrade</option>
              {later.map((r) => (
                <option key={r.id} value={r.id}>{r.display}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {instanceNote && profile.data?.product && <p className="settings-note">{instanceNote}</p>}
      {note && profile.data?.product && <p className="settings-note">{note}</p>}
      {!canSet && profile.data?.product && (
        <p className="settings-note">Set by this engagement&rsquo;s approvers.</p>
      )}
      {said && <p className="settings-said" role="status">{said}</p>}
    </>
  )
}
